import React from 'react';
import Layout from '@/components/admin/Layout/Layout';
import ModelsTable from '@/components/admin/ListAssistants/ListAssistants';
import IAMDashboard from '@/components/admin/IAM/IAMDashboard';
import AIUsageDashboard from '@/components/admin/AIUsageDashboard/AIUsageDashboard';
import { ConversationsHistory } from '@/components/admin/ConversationsHistory';
import { ConversationsFeedback } from '@/components/admin/ConversationsFeedback';
import { KnowledgeCenter } from '@/components/admin/KnowledgeCenter/KnowledgeCenter';
import AgentsDashboard from '@/components/admin/Agents/AgentsDashboard';

import { FeatureFlag, isFeatureEnabled, isAdminEnabled } from '@/config/featureFlags';
import { Navigate } from 'react-router-dom';

type AdminSection = 'assistants' | 'iam' | 'dashboard' | 'conversation-history' | 'conversation-feedback' | 'knowledge-center' | 'agents';

const AdminPage = () => {
  // Double-check if admin is enabled
  if (!isAdminEnabled()) {
    return <Navigate to="/" replace />;
  }

  // Determine available sections based on feature flags
  const getAvailableSections = (): AdminSection[] => {
    const sections: AdminSection[] = [];

    // if (isFeatureEnabled(FeatureFlag.ADMIN_AI_USAGE)) sections.push('dashboard');
    if (isFeatureEnabled(FeatureFlag.ADMIN_ASSISTANTS)) sections.push('assistants');
    if (isFeatureEnabled(FeatureFlag.ADMIN_IAM)) sections.push('iam');
    if (isFeatureEnabled(FeatureFlag.ADMIN_CONVERSATION_HISTORY)) sections.push('conversation-history');
    if (isFeatureEnabled(FeatureFlag.ADMIN_CONVERSATION_FEEDBACK)) sections.push('conversation-feedback');
    // if (isFeatureEnabled(FeatureFlag.ADMIN_KNOWLEDGE_CENTER)) sections.push('knowledge-center');
    // if (isFeatureEnabled(FeatureFlag.ADMIN_AGENTS)) sections.push('agents');

    return sections;
  };

  const availableSections = getAvailableSections();
  const defaultSection = availableSections.length > 0 ? availableSections[0] : 'dashboard';

  const [activeSection, setActiveSection] = React.useState<AdminSection>(defaultSection);

  const renderContent = () => {
    // Only render if feature is enabled
    switch (activeSection) {
      case 'dashboard':
        return isFeatureEnabled(FeatureFlag.ADMIN_AI_USAGE) ? <AIUsageDashboard /> : null;
      case 'assistants':
        return isFeatureEnabled(FeatureFlag.ADMIN_ASSISTANTS) ? <ModelsTable /> : null;
      case 'iam':
        return isFeatureEnabled(FeatureFlag.ADMIN_IAM) ? <IAMDashboard /> : null;
      case 'conversation-history':
        return isFeatureEnabled(FeatureFlag.ADMIN_CONVERSATION_HISTORY) ? <ConversationsHistory /> : null;
      case 'conversation-feedback':
        return isFeatureEnabled(FeatureFlag.ADMIN_CONVERSATION_FEEDBACK) ? <ConversationsFeedback /> : null;
      case 'knowledge-center':
        return isFeatureEnabled(FeatureFlag.ADMIN_KNOWLEDGE_CENTER) ? <KnowledgeCenter /> : null;
      case 'agents':
        return isFeatureEnabled(FeatureFlag.ADMIN_AGENTS) ? <AgentsDashboard /> : null;
      default:
        return availableSections.length > 0 ? <div className="p-4 text-muted-foreground">Please select a section</div> : <div className="p-4 text-muted-foreground">No admin features are enabled in the current configuration.</div>;
    }
  };

  // Don't render if no admin sections are available
  if (availableSections.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Admin Panel Unavailable</h1>
          <p className="text-muted-foreground">No admin features are enabled in the current configuration.</p>
        </div>
      </div>
    );
  }

  return (
    <Layout
      activeSection={activeSection}
      onSectionChange={setActiveSection}
      availableSections={availableSections}
    >
      {renderContent()}
    </Layout>
  );
};

export default AdminPage;